import type { PublicFormConfig, RegisterInput, RegisterResponse } from '@ffs/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { useSlots } from '../hooks/useSlots';
import { ApiError, getSlots, postRegister } from '../lib/api';
import { captureFromDocument } from '../lib/attribution';
import { fireEmailStep, fireRegistered } from '../lib/events';
import type { MountOptions } from '../lib/options';
import { looksLikePhone, type PhoneLib, toE164 } from '../lib/phone';
import { createWidget, loadTurnstile, type Widget } from '../lib/turnstile';
import { detectTimezone } from '../lib/tz';
import {
  customPayload,
  emailError,
  emptyValues,
  type FieldErrors,
  type FormValues,
  isValidEmail,
  mapServerError,
  SLOT_KEY,
  validate,
} from '../lib/validate';
import { CheckField, SelectField, TextField } from './Field';
import { PhoneField } from './PhoneField';
import { choiceOffered, defaultChoice, type SlotChoice, SlotPicker, slotValue } from './SlotPicker';

interface Props {
  cfg: PublicFormConfig;
  o: MountOptions;
  onDone: (res: RegisterResponse) => void;
  onInactive: (message: string) => void;
}

export function RegisterForm({ cfg, o, onDone, onInactive }: Props) {
  const detectedTz = useMemo(() => detectTimezone(), []);
  const [tz, setTz] = useState(detectedTz);
  const [values, setValues] = useState<FormValues>(() =>
    emptyValues({ email: o.email, first_name: o.firstName }),
  );
  const [errors, setErrors] = useState<FieldErrors>({});
  const [message, setMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [choice, setChoice] = useState<SlotChoice | null>(null);
  const [phoneCountry, setPhoneCountry] = useState('US');
  const phoneLib = useRef<PhoneLib | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const tsRef = useRef<HTMLDivElement>(null);
  const widget = useRef<Widget | null>(null);
  const tsFailed = useRef(false);
  const twoStep = cfg.layout !== 'single';
  // 'two_step_slots': the time is chosen on the first step with the email.
  const slotsFirst = cfg.layout === 'two_step_slots';
  // Two-step forms open on the email step unless the host page already supplied a valid email.
  const [step, setStep] = useState<'email' | 'details'>(() =>
    twoStep && !isValidEmail(o.email) ? 'email' : 'details',
  );
  /** Field to focus once the step that holds it has rendered. */
  const focusAfterStep = useRef<string | null>(null);

  const fetcher = useCallback((z: string) => getSlots(o.base, cfg.public_id, z), [o.base, cfg]);
  const { slots, loading, error: slotsError, reload } = useSlots(fetcher, tz);

  // Keep the selection while it is still offered; otherwise fall back to the default pick.
  useEffect(() => {
    if (!slots) return;
    if (choiceOffered(slots, choice)) return;
    const next = defaultChoice(slots);
    if (choice !== null && next !== null) {
      setMessage('The time you picked is no longer offered; we selected the next one for you.');
    }
    setChoice(next);
  }, [slots, choice]);

  // Turnstile widget (FR-REG-11): loaded once per page, rendered per embed.
  useEffect(() => {
    const key = cfg.turnstile_site_key;
    const el = tsRef.current;
    if (!key || !el) return;
    let cancelled = false;
    // Turnstile keeps a zero-height placeholder while it works invisibly; spacing around the
    // widget only applies while it is actually showing a challenge.
    const visible = new ResizeObserver(() =>
      el.toggleAttribute('data-visible', el.getBoundingClientRect().height > 0),
    );
    visible.observe(el);
    loadTurnstile()
      .then((ts) => {
        if (cancelled) return;
        widget.current = createWidget(ts, el, key);
      })
      .catch(() => {
        tsFailed.current = true;
      });
    return () => {
      cancelled = true;
      visible.disconnect();
      widget.current?.remove();
      widget.current = null;
    };
    // Each step renders its own container, so the widget is re-rendered when the step changes.
  }, [cfg.turnstile_site_key, step]);

  useEffect(() => {
    const key = focusAfterStep.current;
    if (!key) return;
    focusAfterStep.current = null;
    formRef.current
      ?.querySelector<HTMLElement>(
        key === 'slot' ? 'input[name="slot"]' : `[id="${CSS.escape(key)}"]`,
      )
      ?.focus();
  }, [step]);

  const set = <K extends keyof FormValues>(k: K, v: FormValues[K]) =>
    setValues((s) => ({ ...s, [k]: v }));
  const setCustom = (k: string, v: string | boolean) =>
    setValues((s) => ({ ...s, custom: { ...s.custom, [k]: v } }));
  const clearError = (k: string) =>
    setErrors((e) => {
      if (!(k in e)) return e;
      const { [k]: _, ...rest } = e;
      return rest;
    });

  const focusFirstError = (errs: FieldErrors) => {
    const form = formRef.current;
    if (!form) return;
    // The email lives on the first step: go back there to fix it.
    if (twoStep && (errs.email || (slotsFirst && errs[SLOT_KEY])) && step !== 'email') {
      focusAfterStep.current = errs.email ? 'email' : 'slot';
      setStep('email');
      return;
    }
    const order = [
      'first_name',
      'last_name',
      'email',
      'phone',
      ...cfg.fields.map((f) => `custom.${f.key}`),
      'sms_consent',
      SLOT_KEY,
    ];
    for (const key of order) {
      if (!errs[key]) continue;
      const el = form.querySelector<HTMLElement>(
        key === SLOT_KEY ? 'input[name="slot"]' : `[id="${CSS.escape(key)}"]`,
      );
      if (el) {
        el.focus();
        return;
      }
    }
  };

  const PHONE_INVALID = 'Enter a valid phone number, including the area code';
  /** Strict check with the country rules; a loose one if they could not load (the server re-checks). */
  const checkPhone = (): string | null => {
    const text = values.phone.trim();
    if (cfg.phone === 'off' || !text) return null;
    const lib = phoneLib.current;
    const ok = lib ? toE164(lib, text, phoneCountry) !== null : looksLikePhone(text);
    return ok ? null : PHONE_INVALID;
  };

  const onSubmit = async (e: Event) => {
    e.preventDefault();
    if (submitting) return;
    setMessage(null);
    if (step === 'email') {
      const err = emailError(values.email);
      if (err) {
        setErrors((x) => ({ ...x, email: err }));
        formRef.current?.querySelector<HTMLElement>('#email')?.focus();
        return;
      }
      if (slotsFirst && choice === null && (slots?.length ?? 0) > 0) {
        setErrors((x) => ({ ...x, [SLOT_KEY]: 'Choose a time' }));
        formRef.current?.querySelector<HTMLElement>('input[name="slot"]')?.focus();
        return;
      }
      fireEmailStep(cfg.public_id, cfg.webinar.slug);
      focusAfterStep.current = 'first_name';
      setStep('details');
      return;
    }
    const errs = validate(values, cfg, choice !== null, (slots?.length ?? 0) > 0);
    const phoneError = checkPhone();
    if (phoneError && !errs.phone) errs.phone = phoneError;
    setErrors(errs);
    if (Object.keys(errs).length) {
      focusFirstError(errs);
      return;
    }
    setSubmitting(true);
    try {
      let turnstile_token: string | undefined;
      if (cfg.turnstile_site_key) {
        if (!widget.current) {
          throw new ApiError(
            0,
            'turnstile_unavailable',
            tsFailed.current
              ? 'Verification could not load. Disable content blockers and try again.'
              : 'Verification is still loading. Please try again in a moment.',
          );
        }
        try {
          turnstile_token = await widget.current.token();
        } catch {
          throw new ApiError(0, 'turnstile_failed', 'Verification failed. Please try again.');
        }
      }
      const input: RegisterInput = {
        form_id: cfg.public_id,
        email: values.email.trim(),
        first_name: values.first_name.trim(),
        slot_start_at: choice as SlotChoice,
        timezone: tz,
        attribution: captureFromDocument(),
        turnstile_token,
      };
      if (cfg.last_name !== 'off' && values.last_name.trim())
        input.last_name = values.last_name.trim();
      if (cfg.phone !== 'off' && values.phone.trim()) {
        const lib = phoneLib.current;
        input.phone = (lib && toE164(lib, values.phone, phoneCountry)) || values.phone.trim();
        input.phone_country = phoneCountry;
      }
      if (cfg.sms_consent !== 'off') input.sms_consent = values.sms_consent;
      const custom = customPayload(values, cfg.fields);
      if (custom) input.custom = custom;

      const res = await postRegister(o.base, input, o.hosted);
      fireRegistered(res);
      if (res.redirect_url) {
        location.assign(res.redirect_url);
        return; // keep the button disabled while the page unloads
      }
      onDone(res);
    } catch (err) {
      widget.current?.reset();
      const m = mapServerError(
        err instanceof ApiError ? err : new ApiError(0, 'http_error', 'Something went wrong'),
      );
      if (m.action === 'inactive') {
        onInactive(m.message ?? 'This webinar is not accepting registrations.');
        return;
      }
      if (m.action === 'refresh_slots') void reload();
      setErrors(m.fields);
      setMessage(m.message);
      if (Object.keys(m.fields).length) focusFirstError(m.fields);
    } finally {
      setSubmitting(false);
    }
  };

  const buttonText = o.buttonText ?? cfg.button_text;
  const showLast = cfg.last_name !== 'off';
  const showPhone = cfg.phone !== 'off';

  const emailField = (
    <TextField
      id="email"
      label="Email"
      type="email"
      value={values.email}
      required
      autocomplete="email"
      error={errors.email}
      onInput={(v) => {
        set('email', v);
        clearError('email');
      }}
      onBlur={() => {
        if (values.email.trim() && !isValidEmail(values.email))
          setErrors((e) => ({ ...e, email: 'Enter a valid email address' }));
      }}
    />
  );

  // Rendered on both steps so the verification widget loads while the email is typed.
  const footer = (label: string, disabled: boolean) => (
    <>
      <div class="turnstile" ref={tsRef} />
      <div class="alert" role="alert" aria-live="assertive">
        {message}
      </div>
      <button class="btn" type="submit" disabled={disabled}>
        {label}
      </button>
      {cfg.turnstile_site_key ? (
        <p class="fine fine-shield">
          <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
            <path
              d="M12 2.5 4.5 5.3v6.2c0 4.6 3.1 8.6 7.5 9.9 4.4-1.3 7.5-5.3 7.5-9.9V5.3L12 2.5z"
              fill="currentColor"
              opacity="0.9"
            />
            <path
              d="m8.7 12.2 2.2 2.2 4.4-4.6"
              fill="none"
              stroke="#fff"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          Protected by Cloudflare Turnstile
        </p>
      ) : null}
    </>
  );

  const slotPicker = (
    <SlotPicker
      slots={slots}
      loading={loading}
      error={slotsError}
      fieldError={errors[SLOT_KEY]}
      tz={tz}
      detectedTz={detectedTz}
      selected={choice}
      disabled={submitting}
      onSelect={(c) => {
        setChoice(c);
        clearError(SLOT_KEY);
      }}
      onTzChange={setTz}
    />
  );
  const chosenLabel = slots?.find((s) => slotValue(s) === choice)?.label ?? null;

  if (step === 'email')
    return (
      <form ref={formRef} onSubmit={onSubmit} noValidate>
        <div class="grid">{emailField}</div>
        {slotsFirst ? slotPicker : null}
        {footer('Continue', slotsFirst && slots !== null && slots.length === 0)}
      </form>
    );

  return (
    <form ref={formRef} onSubmit={onSubmit} noValidate aria-busy={submitting}>
      {twoStep ? (
        <p class="as-email">
          Registering as <strong>{values.email.trim()}</strong>
          {slotsFirst && chosenLabel ? (
            <>
              {' '}
              for <strong>{chosenLabel}</strong>
            </>
          ) : null}{' '}
          <button
            type="button"
            class="link"
            disabled={submitting}
            onClick={() => {
              focusAfterStep.current = 'email';
              setStep('email');
            }}
          >
            Change
          </button>
        </p>
      ) : null}
      <div class={`grid${showLast ? ' two' : ''}`}>
        <TextField
          id="first_name"
          label="First name"
          value={values.first_name}
          required
          autocomplete="given-name"
          error={errors.first_name}
          onInput={(v) => {
            set('first_name', v);
            clearError('first_name');
          }}
        />
        {showLast ? (
          <TextField
            id="last_name"
            label="Last name"
            value={values.last_name}
            required={cfg.last_name === 'required'}
            autocomplete="family-name"
            error={errors.last_name}
            onInput={(v) => {
              set('last_name', v);
              clearError('last_name');
            }}
          />
        ) : null}
      </div>
      <div class="grid">
        {twoStep ? null : emailField}
        {showPhone ? (
          <PhoneField
            base={o.base}
            value={values.phone}
            country={phoneCountry}
            required={cfg.phone === 'required'}
            error={errors.phone}
            onLib={(lib) => {
              phoneLib.current = lib;
            }}
            onChange={(v, country) => {
              set('phone', v);
              setPhoneCountry(country);
              clearError('phone');
            }}
            onBlur={() => {
              const err = checkPhone();
              if (err) setErrors((e) => ({ ...e, phone: err }));
            }}
          />
        ) : null}
        {cfg.fields.map((f) => {
          const key = `custom.${f.key}`;
          const v = values.custom[f.key];
          if (f.type === 'checkbox')
            return (
              <CheckField
                key={key}
                id={key}
                label={f.label}
                checked={v === true}
                required={f.required}
                error={errors[key]}
                onChange={(c) => {
                  setCustom(f.key, c);
                  clearError(key);
                }}
              />
            );
          if (f.type === 'select')
            return (
              <SelectField
                key={key}
                id={key}
                label={f.label}
                value={typeof v === 'string' ? v : ''}
                options={f.options ?? []}
                required={f.required}
                error={errors[key]}
                onChange={(val) => {
                  setCustom(f.key, val);
                  clearError(key);
                }}
              />
            );
          return (
            <TextField
              key={key}
              id={key}
              label={f.label}
              value={typeof v === 'string' ? v : ''}
              required={f.required}
              error={errors[key]}
              onInput={(val) => {
                setCustom(f.key, val);
                clearError(key);
              }}
            />
          );
        })}
        {cfg.sms_consent !== 'off' && showPhone ? (
          <CheckField
            id="sms_consent"
            label={cfg.sms_consent_text ?? 'Send me text message reminders for this session.'}
            checked={values.sms_consent}
            required={cfg.sms_consent === 'required'}
            error={errors.sms_consent}
            onChange={(c) => {
              set('sms_consent', c);
              clearError('sms_consent');
            }}
          />
        ) : null}
      </div>

      {slotsFirst ? null : slotPicker}

      {footer(
        submitting ? 'Saving…' : buttonText,
        submitting || (slots !== null && slots.length === 0),
      )}
    </form>
  );
}
